"use client";

import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { Newspaper } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SectionSkeleton } from "@/components/section-skeleton";
import { PortfolioNewsSummary, PortfolioNewsFeed } from "@/components/portfolio-news/portfolio-news-view";
import { getLocalPortfolio, LOCAL_PORTFOLIO_EVENTS } from "@/lib/local-portfolio/store";
import type { DepotNewsItem } from "@/lib/portfolio/portfolio-news";
import type { PortfolioNewsInsight } from "@/lib/portfolio/portfolio-news-ai";

interface PortfolioNewsResponse {
  news: DepotNewsItem[];
  insight: PortfolioNewsInsight | null;
}

/**
 * "Meine News" fuer Gaeste (kein Login, Depot liegt im localStorage) - rein
 * clientseitiges Pendant zum eingeloggten Pfad in app/meine-news/page.tsx, gleiches
 * Grundmuster wie components/portfolio/guest-dashboard.tsx: liest die lokalen
 * Depot-Symbole, holt den Rest (Namen/News/KI-Synthese) ueber einen einzigen
 * Server-Roundtrip (/api/portfolio-news), da sowohl der Yahoo-News-Abruf als auch der
 * Gemini-Aufruf einen Server-Kontext brauchen.
 */
export function GuestPortfolioNews() {
  const [symbols, setSymbols] = useState<string[]>([]);
  const [data, setData] = useState<PortfolioNewsResponse | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const reload = useCallback(() => {
    const localSymbols = Array.from(new Set(getLocalPortfolio().map((p) => p.symbol)));
    setSymbols(localSymbols);
    setError(false);

    if (localSymbols.length === 0) {
      setData(null);
      setLoaded(true);
      return;
    }

    setLoaded(false);
    fetch("/api/portfolio-news", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ symbols: localSymbols }),
    })
      .then((res) => (res.ok ? (res.json() as Promise<PortfolioNewsResponse>) : Promise.reject()))
      .then((json) => {
        setData(json);
        setLoaded(true);
      })
      .catch(() => {
        setLoaded(true);
        setError(true);
      });
  }, []);

  useEffect(() => {
    // setState nicht synchron im Effect-Body (react-hooks/set-state-in-effect), gleiches
    // Muster wie guest-dashboard.tsx.
    const id = setTimeout(reload, 0);
    const handler = () => reload();
    for (const event of LOCAL_PORTFOLIO_EVENTS) window.addEventListener(event, handler);
    return () => {
      clearTimeout(id);
      for (const event of LOCAL_PORTFOLIO_EVENTS) window.removeEventListener(event, handler);
    };
  }, [reload]);

  // [Punkt 20]: vorher renderte der Ladezustand komplett nichts (kein Spinner, keine
  // Skeleton) - "lange Wartezeit ohne jedes Feedback". Gleiches Skeleton-Muster wie der
  // eingeloggte Pfad (app/meine-news/page.tsx Suspense-Fallback), statt eines neuen.
  if (!loaded) {
    return (
      <div className="flex flex-col gap-6">
        <SectionSkeleton contentHeight="h-32" />
        <SectionSkeleton contentHeight="h-96" />
      </div>
    );
  }

  if (symbols.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
          <Newspaper className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
          <p className="max-w-md text-sm text-muted-foreground">
            Trag zuerst Positionen in deinem Depot ein - dann zeigen wir hier ausschließlich
            News, die tatsächlich etwas mit deinen Aktien zu tun haben.
          </p>
          <Button size="sm" asChild>
            <Link href="/dashboard?tab=depot">Zu Mein Depot</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {error && (
        <Card className="border-negative/40 bg-negative/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3 text-xs text-negative">
            <span>Deine Depot-News konnten gerade nicht geladen werden.</span>
            <button
              type="button"
              onClick={reload}
              className="font-medium underline underline-offset-2 hover:no-underline"
            >
              Erneut versuchen
            </button>
          </CardContent>
        </Card>
      )}
      {data?.insight && <PortfolioNewsSummary insight={data.insight} news={data.news} />}
      <PortfolioNewsFeed news={data?.news ?? []} />
    </div>
  );
}
